import React from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import checkIcon from '@images/checkout/challenger/check.svg';

import {
  TABLET_UP as DESKTOP,
  MOBILE,
  TABLET,
  TABLET_UP,
  DESKTOP as WIDE_DESKTOP,
} from './media';
import { CHALLENGER as T } from './tokens';

export const Page = styled.div`
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};

  .${T.layout.surfaceClassName} & {
    background: transparent;
  }
`;

export const MainRow = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 1424px;
  margin: 0 auto;

  ${MOBILE} {
    flex-direction: column;
    justify-content: flex-start;
    gap: 0;
    /* Clear fixed mobile sticky Reserve bar (~110px). */
    padding: 0 0 140px;
  }

  ${TABLET_UP} {
    justify-content: flex-end;
    gap: ${T.layout.columnGap};
    padding: 0 0 ${T.space[64]};
  }
`;

export const ContentColumn = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  ${MOBILE} {
    width: 100%;
    max-width: none;
    padding: ${T.space[24]} ${T.space[16]} 0;
    gap: ${T.space[32]};
  }

  /** Tablet (Figma 1024 / 2295:47063): cards use full column width; 24px side inset. */
  ${TABLET} {
    width: 100%;
    max-width: none;
    padding: 48px ${T.space[24]} 0;
    gap: ${T.space[48]};
  }

  /** Desktop: 110px left pad; cards stay 720px wide. */
  ${WIDE_DESKTOP} {
    max-width: none;
    padding: 48px 0 0 ${T.layout.contentPaddingLeft};
    gap: ${T.space[48]};

    > * {
      max-width: ${T.layout.contentWidth};
      width: 100%;
    }
  }
`;

export const SummaryColumn = styled.aside`
  flex-shrink: 0;
  align-self: flex-start;

  ${MOBILE} {
    display: none;
  }

  ${TABLET} {
    display: block;
    padding: 48px ${T.space[24]} 0 0;
    position: sticky;
    top: 73px;
  }

  ${WIDE_DESKTOP} {
    display: block;
    padding: 48px 128px 0 48px;
    position: sticky;
    top: 73px;
  }
`;

export const SectionStack = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
`;

/**
 * Figma Heading/H3 (707:10051 “Storage plan”): Avenir Next Medium, 20/24,
 * text-primary #040711.
 */
export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Medium};
  font-size: 20px;
  line-height: 24px;
  color: ${T.color.textPrimary};
  letter-spacing: 0;

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }
`;

export const SectionIntro = styled.p`
  margin: 0;
  max-width: 420px;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textTertiary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }
`;

export const Card = styled.div`
  background: ${T.color.bgPrimary};
  border-radius: ${T.radius.md};
  width: 100%;
  box-sizing: border-box;

  ${MOBILE} {
    padding: ${T.space[16]};
  }

  ${DESKTOP} {
    padding: ${T.space[24]};
  }
`;

export const CardPadded = styled(Card)`
  ${MOBILE} {
    padding: ${T.space[16]} ${T.space[16]} ${T.space[24]};
  }

  ${DESKTOP} {
    padding: ${T.space[24]} ${T.space[24]} ${T.space[32]};
  }
`;

export const Question = styled.h3`
  margin: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 24px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const HelpText = styled.p`
  margin: 0;
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textTertiary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/**
 * Figma text link (707:10053 “Learn more”): Demi Bold 14/21, brand teal,
 * content-width with 2×4 padding — not full-bleed / centered.
 */
export const BrandLink = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: max-content;
  background: none;
  border: none;
  padding: 2px 4px;
  margin: 0;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 21px;
  color: ${T.color.linkBrand};
  text-align: left;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
  }
`;

/**
 * Inline body link (e.g. “Storage calculator”): Body/Medium 700 —
 * Avenir Next Demi Bold 14/19, text-link-brand #0B7A7A.
 */
export const InlineBrandLink = styled.button`
  appearance: none;
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-style: normal;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.linkBrand};
  text-decoration: none;
  vertical-align: baseline;

  &:hover {
    opacity: 0.85;
  }
`;

export const OutlineButton = styled.button`
  appearance: none;
  background: ${T.color.bgPrimary};
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.sm};
  padding: ${T.space[12]} ${T.space[16]};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  color: ${T.color.textPrimary};
  white-space: nowrap;

  ${MOBILE} {
    font-size: 12px;
    line-height: 14px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 18px;
  }

  &:hover {
    border-color: ${T.color.borderBrand};
  }
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${T.color.bgPill};
  color: ${T.color.textInverse};
  border-radius: ${T.radius.pill};
  padding: 2px 6px;
  font-size: 9px;
  font-weight: ${FontWeight.Semibold};
  line-height: normal;
  white-space: nowrap;
`;

/** Figma Banner-large (1052:17287) — brand-secondary, pad 12×16, gap 4. */
export const Banner = styled.div`
  background: ${T.color.bgBrandSecondary};
  border-radius: ${T.radius.md};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[4]};
  width: 100%;
  box-sizing: border-box;
  padding: ${T.space[12]} ${T.space[16]};
`;

/** Figma: Demi Bold 14/19 text-secondary. */
export const BannerTitle = styled.div`
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[4]};
  width: 100%;
`;

/** Outer 16×16 hit box; leaf is Figma check (≈11.7×8.9). */
const CheckIconBox = styled.span`
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CheckIcon = styled.img`
  width: 12px;
  height: 9px;
  display: block;
`;

const CheckLabel = styled.div`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textSecondary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

type SelectRowProps = {
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  price?: React.ReactNode;
  badge?: React.ReactNode;
  children?: React.ReactNode;
};

const SelectButton = styled.button<{ selected?: boolean; disabled?: boolean }>`
  appearance: none;
  width: 100%;
  text-align: left;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: ${T.radius.md};
  border: 1px solid
    ${({ selected }) =>
      selected ? T.color.borderBrand : T.color.borderDefault};
  background: ${({ selected }) =>
    selected ? T.color.bgSelected : T.color.bgPrimary};
  box-sizing: border-box;
  font-family: ${T.font.sans};

  ${MOBILE} {
    padding: ${T.space[16]} ${T.space[12]};
  }

  ${DESKTOP} {
    padding: ${T.space[16]};
  }
`;

const SelectBody = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SelectTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[4]};
  flex-wrap: wrap;
`;

const SelectTitle = styled.div`
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SelectDescription = styled.div`
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textTertiary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SelectPrice = styled.div`
  flex-shrink: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const SelectRow: React.FC<SelectRowProps> = ({
  selected,
  disabled,
  onClick,
  title,
  description,
  price,
  badge,
  children,
}) => (
  <SelectButton
    type="button"
    selected={selected}
    disabled={disabled}
    aria-pressed={selected}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
  >
    <SelectBody>
      <SelectTitleRow>
        <SelectTitle>{title}</SelectTitle>
        {badge}
      </SelectTitleRow>
      {description && <SelectDescription>{description}</SelectDescription>}
      {children}
    </SelectBody>
    {price !== undefined && <SelectPrice>{price}</SelectPrice>}
  </SelectButton>
);

export const SelectStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
`;

export const FeatureCheck: React.FC<{
  children: React.ReactNode;
  iconSrc?: string;
}> = ({ children, iconSrc = checkIcon }) => (
  <CheckRow>
    <CheckIconBox>
      <CheckIcon src={iconSrc} alt="" width={12} height={9} />
    </CheckIconBox>
    <CheckLabel>{children}</CheckLabel>
  </CheckRow>
);
